<template lang="pug">
.radio-filter-component
    .search-area(v-if="hasSearch")
        .search-container
            input.search-field(
                :placeholder="searchPlaceholder"
                v-model="search"
                @input="searchValue"
            )
            img.search-icon(
                v-if="!search"
                src="assets/images/search.svg" width="16" height="16"
            )
            span.clear-icon(v-if="search" @click="clearSearch")
    .radio-list(v-infinite-scroll="loadMoreData" ref="scrollList")
        .radio-item(v-for="option in filteredOptions" :key="option.id")
            radio-btn(:value="isChecked(option)" @input="toggleOption(option)")
                | {{ option.displayName || option.name }}
        .empty-state(v-if="!isLoading && options.length === 0")
            | No options found
        .load-more-indicator(v-if="isLoadingMore" ref="load")
            .small-loader
                .loader-dot
                .loader-dot
                .loader-dot
</template>

<script>
import _ from "lodash";
import RadioBtn from "@/components/shared/RadioBtn.vue";
import { InfiniteScroll } from "@/utils/directives";
import { loadOptionsHelper } from "../helpers";
import { handleActionError } from "@/utils";

export default {
  name: "RadioBtnFilter",
  components: { RadioBtn },
  directives: { InfiniteScroll },
  props: {
    filterId: {
      type: String,
      required: true,
    },
    tenant: {
      type: String,
      required: true,
    },
    hasSearch: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: "Search...",
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      options: [],
      search: "",
      isLoading: false,
      isLoadingMore: false,
      offset: 0,
      limit: 10,
      canLoadMore: true,
    };
  },
  computed: {
    filteredOptions() {
      if (this.search) {
        return this.options;
      }

      return this.options;
    },
    selectedItem() {
      return this.selectedItems.length > 0 ? this.selectedItems[0] : null;
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newData) {
        if (newData?.length > 0) {
          this.options = [...newData];
        } else {
          this.loadOptions();
        }
      },
    },
    tenant: {
      handler(newTenant, oldTenant) {
        if (newTenant !== oldTenant) {
          this.resetAndLoadOptions();
        }
      },
    },
  },
  created() {
    if (!this.data?.length) {
      this.loadOptions();
    }
  },
  methods: {
    resetAndLoadOptions() {
      this.options = [];
      this.offset = 0;
      this.canLoadMore = true;
      this.isLoading = false;
      this.isLoadingMore = false;
      this.loadOptions();
    },
    async loadOptions() {
      if (!this.canLoadMore || this.isLoading || this.isLoadingMore) {
        return;
      }

      if (this.offset === 0) {
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }

      try {
        const options = await loadOptionsHelper({
          optionsId: this.filterId,
          tenant: this.tenant,
          limit: this.limit,
          offset: this.offset,
          q: this.search || undefined,
        });

        if (options && options.length > 0) {
          this.options = this.offset === 0 ? options : [...this.options, ...options];
          this.offset += this.limit;

          this.canLoadMore = options.length >= this.limit;
        } else {
          this.canLoadMore = false;
        }
      } catch (error) {
        handleActionError(error, this, "getting options");
        this.canLoadMore = false;
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },
    loadMoreData() {
      this.loadOptions();
    },
    searchValue: _.debounce(function () {
      this.options = [];
      this.offset = 0;
      this.canLoadMore = true;
      this.isLoading = false;
      this.isLoadingMore = false;
      this.loadOptions();

      if (this.$refs.scrollList) {
        this.$refs.scrollList.scrollTop = 0;
      }
    }, 350),
    clearSearch() {
      this.search = "";
      this.searchValue();
    },
    isChecked(option) {
      return this.selectedItem === option.id;
    },
    toggleOption(option) {
      const newSelectedItems = this.selectedItem === option.id ? [] : [option.id];

      this.$emit("selection-change", {
        filterId: this.filterId,
        selectedItems: newSelectedItems,
      });
    },
    clear() {
      this.$emit("selection-change", {
        filterId: this.filterId,
        selectedItems: [],
      });
    },
    getSelectedItems() {
      return [...this.selectedItems];
    },
    setSelectedItems(items) {
      const selectedItems = items && items.length > 0 ? [items[0]] : [];

      this.$emit("selection-change", {
        filterId: this.filterId,
        selectedItems,
      });
    },
  },
};
</script>

<style lang="stylus" scoped>
.radio-filter-component
  .search-area
    margin 6px 0

  .search-container
    display flex
    align-items center
    width 100%
    border 1px solid #DDDDDD
    border-radius 4px
    padding-right 8px

    .search-field
      width 100%
      padding 6px 8px
      font-size 13px
      outline none
      border none
      color text-color
      background-color transparent

    .search-icon
      width 16px
      height 16px
      margin-left 8px
      cursor pointer
      opacity 0.7

    .clear-icon
      width 16px
      margin-left 8px
      cursor pointer
      position relative
      opacity 0.7
      color #303030

      &:before, &:after
        content ''
        position absolute
        top 0
        left 50%
        width 100%
        height 2px
        background-color #303030
        transform-origin center
      &:before
        transform translateX(-50%) rotate(45deg)
      &:after
        transform translateX(-50%) rotate(-45deg)
      &:hover
        opacity 1

  .divider
    height 1px
    background-color #e0e0e0
    margin 10px 0

  .radio-list
    max-height 200px
    overflow-y auto

    &::-webkit-scrollbar
      width 6px
    &::-webkit-scrollbar-thumb
      border-radius 10px
      width 6px
      height auto
      background-color #d8d8d8

  .radio-item
    margin-bottom 4px
    padding 2px 0

    label
      margin 0
      cursor pointer
      font-weight normal
      font-size 14px

  .empty-state
    text-align center
    color #999
    padding 10px 0

  .load-more-indicator
    display flex
    justify-content center
    padding 10px

    .small-loader
      display flex
      align-items center
      gap 4px

      .loader-dot
        width 6px
        height 6px
        border-radius 50%
        background-color button-background-color
        animation bounce-loader 1.4s ease-in-out infinite both

        &:nth-child(1)
          animation-delay -0.32s

        &:nth-child(2)
          animation-delay -0.16s

@keyframes bounce-loader
  0%, 80%, 100%
    transform scale(0)
  40%
    transform scale(1)
</style>
